import { createClient } from "@sanity/client";
import { TAGS } from "../constants";
import imageUrlBuilder from "@sanity/image-url";
import { SanityImageSource } from "@sanity/image-url/lib/types/types";
import { InterviewArticle, NewsArticle, PostArticle } from "./types";
import { OptimizedArticle } from "../article-utils";
import { cache } from "react";
import { selectFeaturedTopStories } from "../editorial-discovery";

export const client = createClient({
  projectId: "wn8y9ec7", // replace with your Sanity project ID
  dataset: "production", // or your dataset name
  apiVersion: "2023-08-15", // use the latest API version
  //   useCdn: false, //keep it false and check the next-sanity docs for more info
  // useCdn: process.env.NODE_ENV === "production",
  useCdn: false,
  // Prevent third-party cookies
  withCredentials: false,
});

// Get a pre-configured url-builder from your sanity client
const builder = imageUrlBuilder(client);

// Then we like to make a simple function like this that gives the
// builder an image and returns the builder for you to specify additional
// parameters:
export function urlFor(source: SanityImageSource) {
  return builder.image(source);
}

// uses GROQ to query content: https://www.sanity.io/docs/groq
//NEWS ARTICLES
export async function getNewsArticles() {
  const posts = await client.fetch<NewsArticle[]>(
    `*[_type == "newsArticle"] | order(publishedAt desc) {
      ...,
      author->{
        _id,
        name,
        slug,
        bio,
        role,
        tagline,
        image
      }
    }`,
    {},
    {
      cache: "force-cache",
      next: {
        tags: [TAGS.newsArticles],
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );
  return posts;
}

// Optimized version for news page (pre-sorted, minimal data)
export async function getOptimizedNewsArticles(): Promise<OptimizedArticle[]> {
  const posts = await client.fetch<NewsArticle[]>(
    `*[_type == "newsArticle"] | order(publishedAt desc) {
      _id,
      _type,
      _updatedAt,
      title,
      subtitle,
      slug,
      publishedAt,
      customUpdatedAt,
      mainImage,
      main_image_alt_text,
      "imageUrl": mainImage.asset->url
    }`,
    {},
    {
      cache: "force-cache",
      next: {
        tags: [TAGS.newsArticles],
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );

  return posts.map(optimizeArticleData);
}

export async function getNewsArticle(slug: string) {
  const post = await client.fetch<NewsArticle>(
    `*[_type == "newsArticle" && slug.current == $slug][0]{
      ...,
      author->{
        _id,
        name,
        slug,
        bio,
        role,
        tagline,
        image
      }
    }`,
    { slug },
    {
      next: {
        tags: [`newsArticle:${slug}`], // Add a unique tag for the article
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );
  return post;
}

// POSTS ARTICLES
export async function getPostArticles() {
  const posts = await client.fetch<PostArticle[]>(
    `*[_type == "postArticle"]{
      ...,
      author->{
        _id,
        name,
        slug,
        bio,
        role,
        tagline,
        image
      }
    }`,
    {},
    {
      next: {
        tags: [TAGS.postArticles],
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );
  return posts;
}

export async function getPostArticle(slug: string) {
  const post = await client.fetch<PostArticle>(
    `*[_type == "postArticle" && slug.current == $slug][0]{
      ...,
      author->{
        _id,
        name,
        slug,
        bio,
        role,
        tagline,
        image
      }
    }`,
    { slug },
    {
      next: {
        tags: [`postArticle:${slug}`], // Add a unique tag for the article
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );
  return post;
}

export async function getRelatedPostArticles(tags: string[]) {
  const posts = await client.fetch<PostArticle[]>(
    '*[_type == "postArticle" && tags[] in $tags]',
    { tags },
    {
      next: {
        tags: [`relatedPostArticles:${tags}`], // Add a unique tag for the article
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );
  return posts;
}

export async function getRelatedNewsArticles(tags: string[]) {
  const news = await client.fetch<NewsArticle[]>(
    '*[_type == "newsArticle" && tags[] in $tags]',
    { tags },
    {
      next: {
        tags: [`relatedNewsArticles:${tags}`], // Add a unique tag for the article
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );
  return news;
}

//all
export async function getInterviewArticles() {
  const interviews = await client.fetch<InterviewArticle[]>(
    '*[_type == "interviewArticle"]',
    {},
    {
      next: {
        tags: [TAGS.interviewArticles],
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );
  return interviews;
}

//single
export async function getInterviewArticle(slug: string) {
  const interview = await client.fetch<InterviewArticle>(
    '*[_type == "interviewArticle" && slug.current == $slug][0]',
    { slug },
    {
      next: {
        tags: [`interviewArticle:${slug}`], // Add a unique tag for the article
        revalidate: 3600, // Refresh automatically even if a publication webhook is delayed.
      },
    }
  );

  return interview;
}

export const getContentByTag = cache(async (tag: string) => {
  // `tag` at the top level is Sanity's request tag, which rejects spaces.
  // Editorial tags belong in the query parameters. Use exact tag membership
  // so a tag such as "art" cannot accidentally match unrelated stories.
  const articles = await client.fetch<
    Array<(NewsArticle | PostArticle | InterviewArticle) & { _type: string }>
  >(
    `*[_type in ["newsArticle", "postArticle", "interviewArticle"] &&
      !(_id in path("drafts.**")) && defined(slug.current) &&
      (!defined(publishedAt) || dateTime(publishedAt) <= dateTime(now())) &&
      count(tags[lower(@) == $editorialTag]) > 0] | order(publishedAt desc)[0...200]`,
    { editorialTag: tag.trim().toLowerCase() },
    { next: { revalidate: 3600, tags: [TAGS.newsArticles, TAGS.postArticles, TAGS.interviewArticles] } },
  );
  return {
    newsArticles: articles.filter((a) => a._type === "newsArticle") as NewsArticle[],
    postArticles: articles.filter((a) => a._type === "postArticle") as PostArticle[],
    interviews: articles.filter((a) => a._type === "interviewArticle") as InterviewArticle[],
  };
});

// OPTIMIZED FUNCTIONS FOR HOMEPAGE PERFORMANCE
// These functions pre-compute expensive operations server-side

function optimizeArticleData(article: any): OptimizedArticle {
  const isNews = article._type === "newsArticle";

  // Pre-compute all expensive operations
  // Use customUpdatedAt if available, otherwise publishedAt
  const dateToShow = article.customUpdatedAt || article.publishedAt;
  const badgeDate = new Date(dateToShow).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  // Pre-compute image URL using Sanity's image builder
  let imageUrl = "/images/og/OG.jpg"; // fallback
  if (article.mainImage) {
    try {
      imageUrl = urlFor(article.mainImage).width(1200).height(750).url();
    } catch (error) {
      console.warn("Failed to generate image URL:", error);
    }
  }

  const href = isNews
    ? `/news/${article.slug.current}`
    : `/articles/${article.slug.current}`;

  // Use main_image_alt_text if available, otherwise fall back to title
  const altText = article.main_image_alt_text || article.title;

  return {
    ...article,
    imageUrl,
    badgeDate,
    href,
    isNews,
    altText,
  };
}

// export async function getOptimizedNewsArticles(): Promise<OptimizedArticle[]> {
//   const posts = await client.fetch<NewsArticle[]>(
//     '*[_type == "newsArticle"] | order(publishedAt desc)',
//     {},
//     {
//       next: {
//         tags: [TAGS.newsArticles],
//         revalidate: 3600,
//       },
//     }
//   );
//   return posts.map(optimizeArticleData);
// }

export async function getOptimizedPostArticles(): Promise<OptimizedArticle[]> {
  const posts = await client.fetch<PostArticle[]>(
    `*[_type == "postArticle"] | order(publishedAt desc) {
      _id,
      _type,
      _updatedAt,
      title,
      subtitle,
      slug,
      publishedAt,
      customUpdatedAt,
      mainImage,
      main_image_alt_text,
      "imageUrl": mainImage.asset->url
    }`,
    {},
    {
      next: {
        tags: [TAGS.postArticles],
        revalidate: 3600,
      },
    }
  );
  return posts.map(optimizeArticleData);
}

// ULTRA-OPTIMIZED HOMEPAGE DATA - ALL PROCESSING DONE SERVER-SIDE
export async function getHomepageLatestItems(): Promise<{
  latestWithEventNames: (OptimizedArticle & {
    contentType: string;
    eventName: string;
  })[];
  recentNews: OptimizedArticle[];
  recentArticles: OptimizedArticle[];
}> {
  // Fetch both types in parallel
  const [optimizedNews, optimizedArticles] = await Promise.all([
    getOptimizedNewsArticles(),
    getOptimizedPostArticles(),
  ]);

  // Combine and sort - done once server-side
  // Sort by customUpdatedAt if exists, otherwise publishedAt (prioritizes updated content)
  const allOptimizedContent = [
    ...optimizedNews.map((item) => ({ ...item, contentType: "news" })),
    ...optimizedArticles.map((item) => ({ ...item, contentType: "article" })),
  ].sort((a, b) => {
    const aDate = a.customUpdatedAt || a.publishedAt;
    const bDate = b.customUpdatedAt || b.publishedAt;
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  // Get latest 2 items
  const latest = allOptimizedContent.slice(0, 2);

  // Pre-compute event names
  const latestWithEventNames = latest.map((article) => ({
    ...article,
    eventName:
      article._type === "newsArticle"
        ? "HomePage:latest-news-click"
        : "HomePage:latest-article-click",
  }));

  // Only exclude the hero article (index 0) from recent lists to avoid duplication
  const heroItem = latest[0];
  const heroNewsId = heroItem?.contentType === "news" ? heroItem._id : null;
  const heroArticleId = heroItem?.contentType === "article" ? heroItem._id : null;

  // Get recent items excluding only the hero
  const recentNews = optimizedNews
    .filter((item) => item._id !== heroNewsId)
    .slice(0, 4);

  const recentArticles = optimizedArticles
    .filter((item) => item._id !== heroArticleId)
    .slice(0, 4);

  return {
    latestWithEventNames,
    recentNews,
    recentArticles,
  };
}

// ULTRA-OPTIMIZED SECTIONS PROCESSING - MOVED FROM RENDER TO SERVER-SIDE
export function processSectionsLayout(sections: any[]) {
  const sectionsByLayout = sections.reduce(
    (
      acc: {
        left: any[];
        right: any[];
        mobileTop: any[];
        mobileBottom: any[];
      },
      section: any
    ) => {
      const sectionWithPosition = {
        ...section,
        position: section.position ?? 0,
      };

      if (section.explore_layout_side === "left")
        acc.left.push(sectionWithPosition);
      if (section.explore_layout_side === "right")
        acc.right.push(sectionWithPosition);
      if (section.explore_mobile_layout_side === "top")
        acc.mobileTop.push(sectionWithPosition);
      if (section.explore_mobile_layout_side === "bottom")
        acc.mobileBottom.push(sectionWithPosition);

      return acc;
    },
    { left: [], right: [], mobileTop: [], mobileBottom: [] }
  );

  // Sort once server-side
  (Object.values(sectionsByLayout) as any[][]).forEach((sections: any[]) => {
    sections.sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
  });

  return sectionsByLayout;
}

// FETCH CONTENT BY SLUG AND TYPE (for Featured Top Stories section)
export async function getFeaturedTopStories(items: { slug: string; type: string }[]): Promise<OptimizedArticle[]> {
  const [pinned, recent] = await Promise.all([
    getContentBySlugs(items.slice(0, 1)),
    client.fetch<NewsArticle[]>(
      `*[_type in ["newsArticle", "postArticle"] && !(_id in path("drafts.**")) && !(_id in path("versions.**")) && defined(slug.current) && defined(mainImage.asset) && publishedAt <= now()] | order(publishedAt desc)[0...40]{_id,_type,_updatedAt,title,subtitle,slug,publishedAt,customUpdatedAt,mainImage,main_image_alt_text,"imageUrl":mainImage.asset->url}`,
      {},
      { cache: "force-cache", next: { tags: [TAGS.newsArticles, TAGS.postArticles, "featuredStories"], revalidate: 3600 } },
    ),
  ]);
  return selectFeaturedTopStories([...pinned, ...recent.map(optimizeArticleData)], items[0]?.slug);
}

export async function getContentBySlugs(
  items: { slug: string; type: string }[]
): Promise<OptimizedArticle[]> {
  if (items.length === 0) return [];

  // Group by Sanity _type
  const typeMap: Record<string, string> = {
    news_article: "newsArticle",
    post_article: "postArticle",
    interview_article: "interviewArticle",
  };

  const results = await Promise.all(
    items.map(async (item) => {
      const sanityType = typeMap[item.type] || item.type;
      const article = await client.fetch(
        `*[_type == $type && slug.current == $slug][0]{
          _id,
          _type,
          _updatedAt,
          title,
          subtitle,
          slug,
          publishedAt,
          customUpdatedAt,
          mainImage,
          main_image_alt_text,
          "imageUrl": mainImage.asset->url
        }`,
        { type: sanityType, slug: item.slug },
        {
          cache: "force-cache",
          next: {
            tags: [`${sanityType}:${item.slug}`],
            revalidate: 3600,
          },
        }
      );
      return article ? optimizeArticleData(article) : null;
    })
  );

  return results.filter(Boolean) as OptimizedArticle[];
}

// AUTHORS
export async function getAllAuthors() {
  const authors = await client.fetch(
    `*[_type == "author"] | order(name asc) {
      _id,
      name,
      slug,
      bio,
      role,
      tagline,
      location,
      expertise,
      sameAs,
      image,
      "imageUrl": image.asset->url
    }`,
    {},
    {
      cache: "force-cache",
      next: {
        tags: [TAGS.authors],
        revalidate: 3600,
      },
    }
  );
  return authors;
}

export async function getAuthorByHandle(handle: string) {
  const author = await client.fetch(
    `*[_type == "author" && slug.current == $handle][0]{
      _id,
      name,
      slug,
      bio,
      role,
      tagline,
      location,
      expertise,
      sameAs,
      image,
      "imageUrl": image.asset->url
    }`,
    { handle },
    {
      cache: "force-cache",
      next: {
        tags: [`author:${handle}`],
        revalidate: 3600,
      },
    }
  );
  return author;
}

export async function getArticlesByAuthor(authorId: string) {
  const articles = await client.fetch<PostArticle[]>(
    `*[_type == "postArticle" && author._ref == $authorId] | order(publishedAt desc) {
      ...,
      author->{
        _id,
        name,
        slug,
        bio,
        role,
        tagline,
        image
      }
    }`,
    { authorId },
    {
      cache: "force-cache",
      next: {
        tags: [`authorArticles:${authorId}`],
        revalidate: 3600,
      },
    }
  );
  return articles;
}

export async function getNewsByAuthor(authorId: string) {
  const news = await client.fetch<NewsArticle[]>(
    `*[_type == "newsArticle" && author._ref == $authorId] | order(publishedAt desc) {
      ...,
      author->{
        _id,
        name,
        slug,
        bio,
        role,
        tagline,
        image
      }
    }`,
    { authorId },
    {
      cache: "force-cache",
      next: {
        tags: [`authorNews:${authorId}`],
        revalidate: 3600,
      },
    }
  );
  return news;
}
